import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAllCourts } from 'services/courtServices';
import { getAllInternalStates } from 'services/internalStatusServices';
import {
  getDocumentLink,
  getDailyStateCatalogs,
  saveManagement,
  exportDailyStateExcel,
} from 'services/unholsterDailyStateService';
import usePermissionsDailyState from '../../permissions/modules/dailyState/usePermissionsDailyState';
import { persistStateToUrl } from '../../utils/urlState';
import { parseNumberArrayParam } from '../../utils/parseArray';
import useUnholsterDailyState from 'hooks/useUnholsterDailyState';
import CardStats from '../../components/CardStats/CardStats';
import FilterChips from '../../components/FilterChips/FilterChips';
import ExpandablePanel from '../../components/ExpandablePanel/ExpandablePanel';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { formatDateByLocale } from '../../utils/formatDate';
import { useSnackbar } from 'context/SnackbarContext';
import { dailyStateUnholsterI18n } from './i18n';
import './dailyStateUnholster.css';

const DEFAULT_FILTERS = {
  courts: [],
  areas: [],
  status: null,
  document: null,
  caseState: null,
  myCases: false,
  date: '',
  search: '',
};

const DEFAULT_SORT = { key: 'prioridad', direction: 'desc' };

// 0 no existe en InternalStatusCatalog_SAAS: es como viaja "sin gestionar" al backend.
const SIN_GESTIONAR = 0;
const PENDIENTE = 1;

// Un rol acota mas que un tribunal, asi que sirve de alcance por si solo. El minimo
// evita que dos letras barran las 416.000 causas. Tiene que calzar con TieneAlcance().
const MIN_BUSQUEDA = 3;

// Tri-estado: null en value significa "todos", y no viaja al backend.
const buildTriOptions = (t, claveSi, claveNo) => [
  { label: t('dsu_status_all'), value: null },
  { label: t(claveSi), value: true },
  { label: t(claveNo), value: false },
];

// El catalogo guarda 'info' para No amerita; la vista lo pinta rojo a proposito.
const COLOR_GESTION = { 1: 'warning', 2: 'success', 3: 'error' };

// Mapea el accessor de cada columna a la clave de orden del backend.
const SORTABLE = {
  internalStateStatus: 'estado',
  rol: 'rol',
  titulo: 'causa',
  tribunalNombre: 'tribunal',
  tramite: 'tramite',
  descripcionTramite: 'descripcion',
  fecha: 'fecha',
  internalStatusId: 'gestion',
  businessAreaNombre: 'area',
  documentoId: 'documento',
};

const PAGE_SIZES = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

// Mismas opciones que la vista antigua: Todos + el catalogo.
const buildStatusOptions = (catalogo, t) => [
  { label: t('dsu_status_all'), value: null, color: null },
  ...(catalogo ?? []).map((s) => ({ label: s.name, value: s.id, color: s.badgeColor })),
];

// "Pendiente" en la vista antigua incluye las filas sin gestionar, que ahi salen como Pendiente.
const statusIdsParaFiltro = (opcion) => {
  if (!opcion || opcion.value === null) return [];
  return opcion.value === PENDIENTE ? [SIN_GESTIONAR, PENDIENTE] : [opcion.value];
};

const DailyStateUnholster = ({ DesignSystemComponents }) => {
  const { language } = useLanguage();
  const locale = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: dailyStateUnholsterI18n });
  const { showSnackbar } = useSnackbar();

  const { Card, Table, ComboBox, Input, Button, IconButton, SvgIcon, Dropdown, Badge } =
    DesignSystemComponents;

  const { canManage, canDownloadExcel, canGoToCause } = usePermissionsDailyState();

  const { data, total, counters, meta, isLoading, error, fetchDailyState, patchRow } =
    useUnholsterDailyState();

  const [searchParams, setSearchParams] = useSearchParams();

  // Inicializadores perezosos: al volver del detalle la busqueda queda como estaba.
  const [filters, setFilters] = useState(() => ({
    courts: [],
    areas: [],
    status: null,
    document: null,
    caseState: null,
    myCases: searchParams.get('misCausas') === 'true',
    date: searchParams.get('fecha') ?? '',
    search: searchParams.get('buscar') ?? '',
  }));
  const [page, setPage] = useState(() => Number(searchParams.get('page') ?? 0));
  const [pageSize, setPageSize] = useState(() => Number(searchParams.get('pageSize') ?? 10));
  const [sort, setSort] = useState(() => ({
    key: searchParams.get('sortKey') ?? DEFAULT_SORT.key,
    direction: searchParams.get('sortDir') ?? DEFAULT_SORT.direction,
  }));
  const [courtOptions, setCourtOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [statusCatalog, setStatusCatalog] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [savingRows, setSavingRows] = useState(() => new Set());
  const [isExporting, setIsExporting] = useState(false);

  // La URL de entrada se congela al montar: el efecto que la escribe corre antes de que
  // lleguen los catalogos y si no, borraria los parametros que todavia no se hidrataron.
  const paramsInicialesRef = useRef(Object.fromEntries(searchParams.entries()));
  const urlHidratadaRef = useRef(false);
  const autoBuscadoRef = useRef(false);
  const veniaConFiltrosRef = useRef(
    Boolean(
      paramsInicialesRef.current.tribunales ||
        paramsInicialesRef.current.areas ||
        paramsInicialesRef.current.misCausas === 'true' ||
        (paramsInicialesRef.current.buscar ?? '').trim().length >= MIN_BUSQUEDA
    )
  );

  const hidratarDesdeUrl = (cortes, areas, estados) => {
    if (urlHidratadaRef.current) return;
    urlHidratadaRef.current = true;

    const url = paramsInicialesRef.current;
    const idsCortes = parseNumberArrayParam(url.tribunales);
    const idsAreas = parseNumberArrayParam(url.areas);

    setFilters((f) => ({
      ...f,
      courts: cortes.filter((c) => idsCortes.includes(c.value)),
      areas: areas.filter((a) => idsAreas.includes(a.value)),
      status: buildStatusOptions(estados, t).find((e) => String(e.value) === url.gestion) ?? null,
      document:
        buildTriOptions(t, 'dsu_document_yes', 'dsu_document_no').find(
          (o) => String(o.value) === url.conDocumento
        ) ?? null,
      caseState:
        buildTriOptions(t, 'dsu_caseActive', 'dsu_caseInactive').find(
          (o) => String(o.value) === url.activas
        ) ?? null,
    }));
  };

  useEffect(() => {
    dailyStateUnholsterI18n.changeLanguage(locale);
  }, [locale]);

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [courts, statuses, catalogs] = await Promise.all([
          getAllCourts(),
          getAllInternalStates(),
          getDailyStateCatalogs(),
        ]);

        const cortes = Array.isArray(courts) ? courts : [];
        const estados = Array.isArray(statuses) ? statuses : [];

        // El backend dice cuales areas son demasiado grandes para buscarlas solas.
        const areas = (catalogs?.areas ?? []).map((a) => ({
          label: a.nombre,
          value: a.id,
          requiereFiltroAdicional: a.requiereFiltroAdicional,
        }));

        setCourtOptions(cortes);
        setStatusCatalog(estados);
        setAreaOptions(areas);

        hidratarDesdeUrl(cortes, areas, estados);
      } catch {
        // Los catalogos vacios degradan los filtros, no la vista. La hidratacion se cierra
        // igual para que la busqueda automatica no quede esperando para siempre.
        hidratarDesdeUrl([], [], []);
      }
    };

    loadCatalogs();
    // Los catalogos se cargan una sola vez; hidratarDesdeUrl se autolimita con un ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busqueda = filters.search.trim();
  const busquedaSirve = busqueda.length >= MIN_BUSQUEDA;

  // Mis causas si acota el universo; documento y estado de la causa no, son filtros.
  const hasScope =
    filters.courts.length > 0 ||
    filters.areas.length > 0 ||
    filters.myCases ||
    busquedaSirve;

  // Un area grande sola abarca mas de 100.000 causas: necesita tribunal o texto.
  const needsExtraFilter =
    filters.courts.length === 0 &&
    !busquedaSirve &&
    !filters.myCases &&
    filters.areas.some((a) => a.requiereFiltroAdicional);

  // Texto empezado pero corto, y sin otro filtro que sostenga la busqueda.
  const busquedaCorta =
    busqueda.length > 0 &&
    !busquedaSirve &&
    !filters.myCases &&
    filters.courts.length === 0 &&
    filters.areas.length === 0;

  const canSearch = hasScope && !needsExtraFilter;

  const buildQuery = useCallback(
    (nextPage, nextPageSize, nextSort) => ({
      tribunalIds: filters.courts.map((c) => c.value),
      businessAreaIds: filters.areas.map((a) => a.value),
      fechaDesde: filters.date || null,
      fechaHasta: null,
      busqueda: filters.search?.trim() || null,
      internalStatusIds: statusIdsParaFiltro(filters.status),
      conDocumento: filters.document?.value ?? null,
      soloActivas: filters.caseState?.value ?? null,
      misCausas: filters.myCases,
      page: nextPage + 1,
      pageSize: nextPageSize,
      sortBy: nextSort.key,
      desc: nextSort.direction === 'desc',
    }),
    [filters]
  );

  const runSearch = useCallback(
    (nextPage = 0, nextPageSize = pageSize, nextSort = sort) => {
      if (!canSearch) return;
      setPage(nextPage);
      fetchDailyState(buildQuery(nextPage, nextPageSize, nextSort));
    },
    [canSearch, pageSize, sort, buildQuery, fetchDailyState]
  );

  useEffect(() => {
    persistStateToUrl({
      filters: {
        tribunales: filters.courts.map((c) => c.value),
        areas: filters.areas.map((a) => a.value),
        gestion: filters.status?.value ?? '',
        conDocumento: filters.document?.value ?? '',
        activas: filters.caseState?.value ?? '',
        misCausas: filters.myCases ? 'true' : '',
        fecha: filters.date,
        buscar: filters.search,
      },
      sortKey: sort.key,
      sortDirection: sort.direction,
      page,
      pageSize,
      setSearchParams,
      replace: true,
    });
  }, [filters, sort, page, pageSize, setSearchParams]);

  // Volver del detalle tiene que reejecutar la busqueda, no solo repintar los filtros.
  useEffect(() => {
    if (autoBuscadoRef.current || !urlHidratadaRef.current) return;
    if (!veniaConFiltrosRef.current || !hasScope) return;
    autoBuscadoRef.current = true;
    runSearch(page, pageSize, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, hasScope, courtOptions, areaOptions]);

  const handlePageChange = (nextPage) => runSearch(nextPage, pageSize);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    runSearch(0, size);
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const next = { key: SORTABLE[key] ?? DEFAULT_SORT.key, direction };
    setSort(next);
    runSearch(0, pageSize, next);
  };

  const handleClearAll = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  const handleRemoveChip = (id) => {
    setFilters((f) => ({
      ...f,
      ...(id === 'courts' && { courts: [] }),
      ...(id === 'areas' && { areas: [] }),
      ...(id === 'date' && { date: '' }),
      ...(id === 'search' && { search: '' }),
      ...(id === 'status' && { status: null }),
      ...(id === 'document' && { document: null }),
      ...(id === 'caseState' && { caseState: null }),
      ...(id === 'myCases' && { myCases: false }),
    }));
  };

  const chips = useMemo(() => {
    const list = [];
    if (filters.search) list.push({ id: 'search', label: t('dsu_chip_search'), value: filters.search });
    if (filters.date) list.push({ id: 'date', label: t('dsu_chip_date'), value: filters.date });
    if (filters.areas.length)
      list.push({ id: 'areas', label: t('dsu_chip_area'), value: filters.areas.map((a) => a.label).join(', ') });
    if (filters.courts.length)
      list.push({ id: 'courts', label: t('dsu_chip_court'), value: filters.courts.map((c) => c.label).join(', ') });
    if (filters.status?.value != null)
      list.push({ id: 'status', label: t('dsu_chip_status'), value: filters.status.label });
    if (filters.document?.value != null)
      list.push({ id: 'document', label: t('dsu_filter_document'), value: filters.document.label });
    if (filters.caseState?.value != null)
      list.push({ id: 'caseState', label: t('dsu_filter_caseState'), value: filters.caseState.label });
    if (filters.myCases)
      list.push({ id: 'myCases', label: t('dsu_filter_myCases'), value: t('dsu_myCases_mine') });
    return list;
  }, [filters, t]);

  const openDocument = useCallback(
    async (documentoId) => {
      try {
        const link = await getDocumentLink(documentoId);

        if (!link?.disponible) {
          showSnackbar(t('dsu_document_unavailable'), 'warning');
          return;
        }

        window.open(link.url, '_blank', 'noopener,noreferrer');
      } catch {
        showSnackbar(t('dsu_document_error'), 'error');
      }
    },
    [showSnackbar, t]
  );

  const statusOptions = useMemo(() => buildStatusOptions(statusCatalog, t), [statusCatalog, t]);
  const documentOptions = useMemo(
    () => buildTriOptions(t, 'dsu_document_yes', 'dsu_document_no'),
    [t]
  );
  const myCasesOptions = useMemo(
    () => [
      { label: t('dsu_myCases_all'), value: false },
      { label: t('dsu_myCases_mine'), value: true },
    ],
    [t]
  );
  const caseStateOptions = useMemo(
    () => buildTriOptions(t, 'dsu_caseActive', 'dsu_caseInactive'),
    [t]
  );

  const rowKey = (row) => `${row.caseId}|${row.fecha}`;

  const handleManage = useCallback(
    async (selected, row) => {
      const key = rowKey(row);
      if (savingRows.has(key) || selected.value === SIN_GESTIONAR) return;

      const previo = {
        internalStatusId: row.internalStatusId,
        internalStatusNombre: row.internalStatusNombre,
        internalStatusColor: row.internalStatusColor,
      };

      setSavingRows((prev) => new Set(prev).add(key));
      patchRow(row.caseId, row.fecha, {
        internalStatusId: selected.value,
        internalStatusNombre: selected.label,
        internalStatusColor: selected.color,
      });

      try {
        await saveManagement({
          caseId: row.caseId,
          rol: row.rol,
          codTribunal: row.codTribunal,
          fecha: row.fecha,
          internalStatusId: selected.value,
        });

        // El modulo viejo cachea el estado diario; si no se invalida muestra lo anterior.
        sessionStorage.removeItem('dailyStateCache');
        window.dispatchEvent(new Event('dailyStateUpdated'));
      } catch (err) {
        patchRow(row.caseId, row.fecha, previo);
        showSnackbar(err?.response?.data?.detail || t('dsu_manage_error'), 'error');
      } finally {
        setSavingRows((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [savingRows, patchRow, showSnackbar, t]
  );

  const handleExportExcel = useCallback(async () => {
    try {
      setIsExporting(true);
      await exportDailyStateExcel(buildQuery(0, pageSize, sort));
    } catch {
      showSnackbar(t('dsu_excel_error'), 'error');
    } finally {
      setIsExporting(false);
    }
  }, [buildQuery, pageSize, sort, showSnackbar, t]);

  const statusBadge = useCallback(
    (row) => {
      const id = row.internalStatusId;
      if (!id) return { label: t('dsu_state_pending'), color: COLOR_GESTION[PENDIENTE] };

      const found = statusCatalog.find((s) => s.id === id);
      return {
        label: row.internalStatusNombre || found?.name || t('dsu_state_pending'),
        color: COLOR_GESTION[id] ?? 'default',
      };
    },
    [statusCatalog, t]
  );

  const columns = useMemo(
    () => [
      {
        accessor: 'internalStateStatus',
        sort: true,
        render: (row) => {
          const active = row.internalStateStatus === 'Activa';
          return (
            <div className='d-flex align-items-center justify-content-center gap-1'>
              {SvgIcon && row.nivelPrioridad > 0 && (
                <SvgIcon
                  name='exclamationTriangle'
                  size='14px'
                  fill='var(--error-500)'
                  title={t('dsu_highPriority')}
                />
              )}
              <span
                className={`dsu__status-dot ${active ? 'is-active' : 'is-inactive'}`}
                title={active ? t('dsu_active') : t('dsu_inactive')}
              />
            </div>
          );
        },
      },
      { accessor: 'rol', label: t('dsu_col_role'), sort: true },
      { accessor: 'titulo', label: t('dsu_col_case'), sort: true },
      { accessor: 'tribunalNombre', label: t('dsu_col_court'), sort: true },
      {
        accessor: 'tramite',
        label: t('dsu_col_process'),
        sort: true,
        render: (row) => <span>{row.tramite || '--'}</span>,
      },
      {
        accessor: 'descripcionTramite',
        label: t('dsu_col_description'),
        sort: true,
        render: (row) => <span>{row.descripcionTramite || '--'}</span>,
      },
      {
        accessor: 'fecha',
        label: t('dsu_col_date'),
        sort: true,
        render: (row) => <span>{formatDateByLocale(row.fecha, locale)}</span>,
      },
      {
        accessor: 'internalStatusId',
        label: t('dsu_col_status'),
        sort: true,
        render: (row) => {
          const { label, color } = statusBadge(row);
          return <Badge text={label} color={color} size='sm' />;
        },
      },
      { accessor: 'businessAreaNombre', label: t('dsu_col_area'), sort: true },
      {
        accessor: 'documentoId',
        label: t('dsu_col_documentShort'),
        sort: true,
        render: (row) => (
          <div className='d-flex justify-content-center'>
            {row.conDocumento && row.documentoId && (
              <IconButton
                ariaLabel={t('dsu_viewDocument')}
                icon='fileEarmarkText'
                size='md'
                onClick={() => openDocument(row.documentoId)}
              />
            )}
          </div>
        ),
      },
      {
        accessor: 'caseId',
        label: t('dsu_col_actions'),
        render: (row) => (
          <div className='d-flex align-items-center gap-1'>
            {canGoToCause && (
              <Link
                to={`/detalle-causa/${row.caseId}/movimientos`}
                state={{
                  caseData: {
                    id: row.caseId,
                    code: row.rol,
                    civilCourtCode: row.civilCourtCode,
                    newCivilCourtCode: row.newCivilCourtCode,
                    businessAreaId: row.businessAreaId,
                  },
                }}
                className='text-decoration-none'
              >
                <IconButton ariaLabel={t('dsu_viewCase')} icon='eye' size='md' />
              </Link>
            )}
            {canManage && Dropdown && (
              <Dropdown
                ariaLabel={t('dsu_manage')}
                icon='threeDotsVertical'
                onSelect={(selected) => handleManage(selected, row)}
                options={statusOptions.filter((o) => o.value !== null)}
                variant='icon'
                disabled={savingRows.has(rowKey(row))}
              />
            )}
          </div>
        ),
      },
    ],
    [
      SvgIcon,
      Dropdown,
      locale,
      statusBadge,
      openDocument,
      canGoToCause,
      canManage,
      statusOptions,
      savingRows,
      handleManage,
      t,
    ]
  );

  const warnings = useMemo(() => {
    if (!meta) return [];
    const list = [];
    if (meta.degradadoDocumentos) list.push(t('dsu_warn_degradedDocuments'));
    if (meta.descripcionTramiteParcial) list.push(t('dsu_warn_partialDescription'));
    if (meta.truncado) list.push(t('dsu_warn_truncated'));
    if ((meta.advertencias ?? []).includes('alcance_demasiado_amplio'))
      list.push(t('dsu_warn_tooManyCases', { tope: meta.topeCausas ?? '' }));
    return list;
  }, [meta, t]);

  return (
    <div>
      <div className='mb-4'>
        <h2 className='fw-semibold'>{t('dsu_title')}</h2>
        <h4 className='fw-normal'>{t('dsu_subtitle')}</h4>
      </div>

      <div className='row mb-4'>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dsu_card_urgent')}
            count={counters.urgentes}
            subtitle={t('dsu_card_unmanaged')}
            icon='exclamationTriangle'
            loading={isLoading}
            fill
            type='error'
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dsu_card_pending')}
            count={counters.pendientes}
            subtitle={t('dsu_card_unmanaged')}
            type='warning'
            icon='clock'
            loading={isLoading}
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dsu_card_resolved')}
            count={counters.gestionadas}
            subtitle={t('dsu_card_managed')}
            icon='check2Circle'
            type='success'
            loading={isLoading}
            fill
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dsu_card_total')}
            count={counters.total}
            subtitle={t('dsu_card_toDate')}
            icon='briefcase'
            loading={isLoading}
          />
        </div>
      </div>

      <Card specialPadding='p-4'>
        <div className='row dsu-filters'>
          <div className='col-3 dsu-f-buscar'>
            <Input
              icon='search'
              name='dsuSearch'
              type='text'
              placeholder={t('dsu_filter_search')}
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </div>
          <div className='col-2 dsu-f-fecha'>
            <Input
              type='date'
              value={filters.date}
              onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))}
            />
          </div>
          <div className='col-2 dsu-f-area'>
            <ComboBox
              placeholder={t('dsu_filter_areas')}
              options={areaOptions}
              value={filters.areas}
              onChange={(v) => setFilters((f) => ({ ...f, areas: v ?? [] }))}
              multiSelect
              width='100%'
              menuPortalTarget={document.body}
            />
          </div>
          <div className='col-2 dsu-f-tribunal'>
            <ComboBox
              placeholder={t('dsu_filter_courts')}
              options={courtOptions}
              value={filters.courts}
              onChange={(v) => setFilters((f) => ({ ...f, courts: v ?? [] }))}
              multiSelect
              width='100%'
              menuPortalTarget={document.body}
            />
          </div>
          <IconButton
            ariaLabel={t('dsu_search')}
            icon='search'
            onClick={() => runSearch(0, pageSize)}
            disabled={!canSearch || isLoading}
            className='mt-1'
            size='lg'
          />
          <IconButton
            ariaLabel={t('dsu_filters')}
            icon='funnel'
            onClick={() => setShowPanel((prev) => !prev)}
            className='mt-1'
            size='lg'
          />
          {canDownloadExcel && (
            <IconButton
              ariaLabel={t('dsu_excel')}
              icon='download'
              onClick={handleExportExcel}
              disabled={!canSearch || isExporting || isLoading}
              className='mt-1'
              size='lg'
            />
          )}
        </div>

        {showPanel && (
          <ExpandablePanel
            open={showPanel}
            showActions
            onApply={() => {
              setShowPanel(false);
              runSearch(0, pageSize);
            }}
            onClear={handleClearAll}
            onClose={() => setShowPanel(false)}
            applyLabel={t('dsu_applyFilters')}
            clearLabel={t('dsu_clearFilters')}
            cancelLabel={t('dsu_closeFilters')}
          >
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <div>
                <h4 className='fw-semibold'>{t('dsu_filterTitle')}</h4>
                <p className='mini font-light fw-normal'>{t('dsu_filterSubtitle')}</p>
              </div>
              <IconButton
                ariaLabel={t('dsu_closeFilters')}
                icon='close'
                size='md'
                onClick={() => setShowPanel(false)}
              />
            </div>

            <div className='row mb-4'>
              <div className='col-3'>
                <Dropdown
                  label={t('dsu_filter_myCases')}
                  options={myCasesOptions}
                  value={filters.myCases ? myCasesOptions[1] : myCasesOptions[0]}
                  onChange={(v) => setFilters((f) => ({ ...f, myCases: v?.value === true }))}
                />
              </div>
              <div className='col-3'>
                <Dropdown
                  label={t('dsu_filter_document')}
                  options={documentOptions}
                  value={filters.document ?? documentOptions[0]}
                  onChange={(v) => setFilters((f) => ({ ...f, document: v ?? null }))}
                />
              </div>
              <div className='col-3'>
                <Dropdown
                  label={t('dsu_filter_caseState')}
                  options={caseStateOptions}
                  value={filters.caseState ?? caseStateOptions[0]}
                  onChange={(v) => setFilters((f) => ({ ...f, caseState: v ?? null }))}
                />
              </div>
              <div className='col-3'>
                <Dropdown
                  label={t('dsu_filter_status')}
                  options={statusOptions}
                  value={filters.status ?? statusOptions[0]}
                  onChange={(v) => setFilters((f) => ({ ...f, status: v ?? null }))}
                />
              </div>
            </div>
          </ExpandablePanel>
        )}

        {!showPanel && (
          <FilterChips
            filters={chips}
            onRemove={handleRemoveChip}
            onClearAll={handleClearAll}
          />
        )}

        {needsExtraFilter && (
          <p className='caption font-light mb-0 mt-2'>{t('dsu_empty_needsExtraFilter')}</p>
        )}

        {busquedaCorta && (
          <p className='caption font-light mb-0 mt-2'>
            {t('dsu_empty_searchTooShort', { min: MIN_BUSQUEDA })}
          </p>
        )}

        {warnings.length > 0 && (
          <div className='mt-3 p-3 rounded border border-warning bg-warning-subtle'>
            {warnings.map((w) => (
              <p key={w} className='mb-0 caption'>
                {w}
              </p>
            ))}
          </div>
        )}

        <div className='table-container dsu-table mt-3'>
          <Table
            columns={columns}
            data={data}
            paginator
            paginationMode='server'
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            paginatorGroup={PAGE_SIZES}
            loading={isLoading}
            serverSortKey={sort.key}
            serverSortDirection={sort.direction}
            onServerSortChange={handleServerSortChange}
          />

          {!isLoading && hasScope && error && (
            <div className='text-center p-4'>
              <p className='caption font-light mb-2'>{t('dsu_empty_error')}</p>
              <Button
                label={t('dsu_retry')}
                variant='naked'
                size='sm'
                onClick={() => runSearch(page, pageSize)}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DailyStateUnholster;
